import React, { useState } from "react";
import { netDelta, driverCause, fmtMoney, fmtSignedMoney, fmtPct } from "../format";
import { describeUsageType } from "../../cost-analyzer/analyzerDimensions";

// Shared grid template so the column header and every row line up. Columns are the
// investigation's existing driver columns (Driver / Cause / Usage-price / Contribution
// / Change), laid out in the cost-analyzer DimensionAttributionPanel's card style.
const COLS = "grid-cols-[1fr_112px_152px_150px_96px]";

function directionWord(d) {
  return d < 0 ? "decrease" : d > 0 ? "increase" : "change";
}

// driverName describes a raw usage_type billing unit; other lenses keep their label.
function driverName(d) {
  const raw = d.label || d.key;
  return d.dimension === "usage_type" ? describeUsageType(raw) : raw;
}

// DriversTab — "what explains the change", in the same visual language as the cost
// analyzer's DimensionAttributionPanel: one card, a header question + total-change
// caption with the Material/All toggle in the top-right, a one-line takeaway, and a
// divided list of the existing driver columns (each with a thin contribution bar).
export default function DriversTab({ drivers = [] }) {
  const [scope, setScope] = useState("material"); // material | all
  const net = netDelta(drivers);
  const dir = directionWord(net);

  const material = [...(drivers || [])]
    .filter((d) => Math.abs(d.deltaCost || 0) >= 0.005)
    .sort((a, b) => Math.abs(b.deltaCost) - Math.abs(a.deltaCost));
  const top = material[0];
  const shown = scope === "material"
    ? material.filter((d) => Math.abs(d.deltaCost || 0) / (Math.abs(net) || 1) >= 0.01)
    : material;
  const hidden = (drivers?.length || 0) - shown.length;

  const share = top ? Math.round(Math.abs(top.sharePct || 0)) : 0;
  const takeaway = top
    ? `${driverName(top)} explains ${share}% of the ${dir} (${fmtSignedMoney(top.deltaCost)}). Price effects are ${driverCause(top).toLowerCase().includes("price") ? "the main factor" : "immaterial by comparison"}.`
    : null;

  return (
    <div className="rounded-card border border-card-bd bg-card p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[16px] font-semibold leading-[22px] text-text">
            What explains the {fmtMoney(Math.abs(net))} {dir}?
          </div>
          <div className="mt-1 text-[12px] text-text-3">
            Top drivers of the change · usage vs price per driver.
          </div>
          <div className="mt-0.5 text-[12px] tabular-nums text-text-3">
            Total change: {fmtSignedMoney(net)} vs previous period
          </div>
        </div>
        {/* Material/All toggle — moved into the header as a segmented control, matching
            the analyzer's "Largest change / Concentration" switch. */}
        <div className="flex shrink-0 items-center gap-1 rounded-seg bg-side p-1 text-[12px]">
          {[["material", "Material drivers"], ["all", "All drivers"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setScope(key)}
              aria-pressed={scope === key}
              className={`box-border rounded-tab border border-solid border-transparent px-3 py-1 transition-colors ${
                scope === key ? "bg-primary-soft-strong font-semibold text-primary shadow-pill" : "text-text-3 hover:text-text-2"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="text-text-3">No drivers with a material change{hidden > 0 ? ` (${hidden} unchanged)` : ""}.</div>
      ) : (
        <>
          {takeaway && (
            <div className="rounded-md bg-surface px-3 py-2 text-[13px] leading-[19px] text-text-2">{takeaway}</div>
          )}

          {/* Column header */}
          <div className={`grid ${COLS} items-center gap-3 px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3 border-b border-solid border-card-bd`}>
            <div>Driver</div>
            <div>Cause</div>
            <div>Usage / price</div>
            <div>Contribution</div>
            <div className="text-right">Change</div>
          </div>

          {/* Rows — a divided list, not separate cards. */}
          <div className="-mt-2">
            {shown.map((d) => {
              const pv = d.priceVolume;
              const pe = Math.abs(pv?.priceEffect || 0);
              const ve = Math.abs(pv?.volumeEffect || 0);
              const isTop = top && String(d.key) === String(top.key);
              const shareAbs = Math.max(0, Math.min(100, Math.abs(Number(d.sharePct) || 0)));
              const metaText = [d.dimension === "usage_type" ? "" : d.dimension, pv?.unit ? describeUsageType(pv.unit) : ""].filter(Boolean).join(" · ");
              return (
                <div
                  key={`${d.dimension}:${d.key}`}
                  data-driver-key={d.key}
                  className={`grid ${COLS} items-center gap-3 border-b border-solid border-card-bd px-3 py-3 last:border-b-0 ${isTop ? "bg-primary-soft" : ""}`}
                >
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-semibold text-text">{driverName(d)}</div>
                    <div className="truncate text-[12px] text-text-3">
                      {isTop && <span className="font-semibold text-primary">Biggest mover{metaText ? " · " : ""}</span>}
                      {metaText}
                    </div>
                  </div>
                  <div className="text-[13px] text-text-2">{driverCause(d)}</div>
                  <div className="text-[12px] tabular-nums text-text-3">
                    {pv && (pe + ve > 0)
                      ? <span><span className="text-text-2">Usage {fmtSignedMoney(pv.volumeEffect)}</span> · Price {fmtSignedMoney(pv.priceEffect)}</span>
                      : "—"}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Bar sized with inline height — the theme overrides the h-* scale, so
                        h-1.5/h-full collapse to 0 and the bar would be invisible. */}
                    <div className="flex-1 overflow-hidden rounded-full bg-surface-2" style={{ height: 6 }}>
                      <div className="rounded-full bg-primary" style={{ height: 6, width: `${shareAbs}%` }} />
                    </div>
                    <span className="shrink-0 tabular-nums text-[14px] font-medium text-text-2" style={{ width: 36, textAlign: "right" }}>
                      {fmtPct(shareAbs)}
                    </span>
                  </div>
                  <span className="justify-self-end tabular-nums text-[15px] font-semibold text-text">
                    {fmtSignedMoney(d.deltaCost)}
                  </span>
                </div>
              );
            })}
          </div>

          {hidden > 0 && (
            <div className="px-3 text-[12px] text-text-3">
              +{hidden} driver{hidden === 1 ? "" : "s"} with a smaller change hidden
            </div>
          )}
        </>
      )}
    </div>
  );
}
